import React, { useState } from 'react';
import { User, Phone, Lock, Save, Eye, EyeOff, ShieldCheck, ShieldOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import QRCode from 'qrcode';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Correctif B7 : le cookie httpOnly porte la session (withCredentials global).
const getAuthHeaders = () => ({});

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Double authentification (cahier des charges labo, §32).
  const [mfaSetup, setMfaSetup] = useState(null); // { secret, otpauth_url, qrDataUrl }
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState(null); // affiches une seule fois
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaDisable, setShowMfaDisable] = useState(false);
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');

  const startMfaSetup = async () => {
    setMfaLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/mfa/setup`, {}, { withCredentials: true });
      const qrDataUrl = await QRCode.toDataURL(r.data.otpauth_url);
      setMfaSetup({ secret: r.data.secret, otpauth_url: r.data.otpauth_url, qrDataUrl });
      setMfaCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de démarrer l'activation");
    } finally {
      setMfaLoading(false);
    }
  };

  const confirmMfaEnable = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/mfa/enable`, { code: mfaCode }, { withCredentials: true });
      setMfaBackupCodes(r.data.backup_codes || []);
      setMfaSetup(null);
      await refreshUser();
      toast.success('Double authentification activée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code invalide');
    } finally {
      setMfaLoading(false);
    }
  };

  const confirmMfaDisable = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/mfa/disable`, { password: mfaDisablePassword }, { withCredentials: true });
      setShowMfaDisable(false);
      setMfaDisablePassword('');
      await refreshUser();
      toast.success('Double authentification désactivée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mot de passe incorrect');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, profileData, { withCredentials: true, headers: getAuthHeaders() });
      await refreshUser();
      toast.success('Profil mis à jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (!/[A-Z]/.test(passwordData.new_password)) {
      toast.error('Le mot de passe doit contenir au moins une majuscule');
      return;
    }
    if (!/[a-z]/.test(passwordData.new_password)) {
      toast.error('Le mot de passe doit contenir au moins une minuscule');
      return;
    }
    if (!/[0-9]/.test(passwordData.new_password)) {
      toast.error('Le mot de passe doit contenir au moins un chiffre');
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Mot de passe modifié');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="settings-page">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] font-['Outfit'] mb-2">Paramètres</h1>
        <p className="text-[#64748B] mb-8">Gérez votre compte et vos préférences</p>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 bg-white rounded-full p-1 border border-[#E2E8F0]">
            <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              Mon profil
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              Sécurité
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-6">Informations personnelles</h2>
              
              <div className="flex items-center gap-4 mb-8 p-4 bg-[#F8FAFC] rounded-xl">
                <div className="w-16 h-16 rounded-full bg-[#E6F4FB] flex items-center justify-center">
                  <span className="text-2xl font-bold text-[#0596DE]">
                    {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">{user?.name}</p>
                  <p className="text-sm text-[#64748B]">{user?.email}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    user?.role === 'admin' ? 'bg-purple-100 text-purple-600' :
                    user?.role === 'doctor' ? 'bg-[#E6F4FB] text-[#0596DE]' :
                    'bg-[#F1F5F9] text-[#475569]'
                  }`}>
                    {user?.role === 'admin' ? 'Administrateur' :
                     user?.role === 'doctor' ? 'Médecin' : 'Patient'}
                  </span>
                </div>
              </div>

              <form onSubmit={handleProfileUpdate} className="space-y-5" data-testid="profile-form">
                <div>
                  <Label htmlFor="name">Nom complet</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="name"
                      value={profileData.name}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      className="pl-10 rounded-xl"
                      data-testid="settings-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      placeholder="+213 5XX XX XX XX"
                      className="pl-10 rounded-xl"
                      data-testid="settings-phone-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={profileLoading}
                  className="rounded-full bg-[#0596DE] hover:bg-[#047CBA]"
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {profileLoading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </form>
            </div>
          </TabsContent>

          <TabsContent value="security">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-6">Changer le mot de passe</h2>
              <form onSubmit={handlePasswordChange} className="space-y-5" data-testid="password-form">
                <div>
                  <Label htmlFor="current">Mot de passe actuel</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="current"
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      className="pl-10 rounded-xl"
                      required
                      data-testid="current-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="newpass">Nouveau mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="newpass"
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      placeholder="••••••••"
                      className="pl-10 pr-10 rounded-xl"
                      required
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-[#64748B] mt-1">Min. 8 caractères, avec 1 majuscule, 1 minuscule et 1 chiffre</p>
                </div>
                <div>
                  <Label htmlFor="confirmpass">Confirmer le nouveau mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="confirmpass"
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      className="pl-10 rounded-xl"
                      required
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={passwordLoading}
                  className="rounded-full bg-[#0596DE] hover:bg-[#047CBA]"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {passwordLoading ? 'Modification...' : 'Changer le mot de passe'}
                </Button>
              </form>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 mt-6">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-1">Double authentification (2FA)</h2>
              <p className="text-sm text-[#64748B] mb-6">Protège votre compte avec un code à usage unique, en plus de votre mot de passe.</p>

              {mfaBackupCodes ? (
                <div>
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-4 text-sm">
                    Notez ces codes de secours dans un endroit sûr. Chacun ne peut être utilisé qu'une seule fois, et ils ne seront plus jamais affichés.
                  </div>
                  <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 mb-4">
                    {mfaBackupCodes.map((c) => <div key={c}>{c}</div>)}
                  </div>
                  <Button onClick={() => setMfaBackupCodes(null)} className="rounded-full bg-[#0596DE] hover:bg-[#047CBA]">
                    J'ai noté mes codes
                  </Button>
                </div>
              ) : mfaSetup ? (
                <form onSubmit={confirmMfaEnable} className="space-y-5">
                  <div className="flex flex-col sm:flex-row items-start gap-5">
                    <img src={mfaSetup.qrDataUrl} alt="QR code d'activation" className="w-40 h-40 rounded-xl border border-[#E2E8F0]" />
                    <div className="text-sm text-[#64748B]">
                      <p className="mb-2">Scannez ce QR code avec une application d'authentification (Google Authenticator, Authy...).</p>
                      <p>Ou saisissez ce code manuellement :</p>
                      <p className="font-mono text-[#0F172A] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 mt-1 inline-block break-all">{mfaSetup.secret}</p>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="mfa-confirm-code">Code de vérification</Label>
                    <Input
                      id="mfa-confirm-code"
                      type="text"
                      inputMode="numeric"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      placeholder="123456"
                      className="mt-1 rounded-xl max-w-[200px] text-center tracking-[0.3em]"
                      required
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" disabled={mfaLoading} className="rounded-full bg-[#0596DE] hover:bg-[#047CBA]">
                      {mfaLoading ? 'Vérification...' : 'Confirmer'}
                    </Button>
                    <Button type="button" variant="outline" className="rounded-full" onClick={() => setMfaSetup(null)}>
                      Annuler
                    </Button>
                  </div>
                </form>
              ) : user?.mfa_enabled ? (
                <div>
                  <div className="flex items-center gap-2 text-[#166534] mb-4">
                    <ShieldCheck className="w-5 h-5" />
                    <span className="font-medium">Activée</span>
                  </div>
                  {showMfaDisable ? (
                    <form onSubmit={confirmMfaDisable} className="space-y-4 max-w-sm">
                      <div>
                        <Label htmlFor="mfa-disable-pass">Confirmez votre mot de passe</Label>
                        <Input
                          id="mfa-disable-pass"
                          type="password"
                          value={mfaDisablePassword}
                          onChange={(e) => setMfaDisablePassword(e.target.value)}
                          className="mt-1 rounded-xl"
                          required
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" disabled={mfaLoading} variant="destructive" className="rounded-full">
                          {mfaLoading ? 'Désactivation...' : 'Confirmer la désactivation'}
                        </Button>
                        <Button type="button" variant="outline" className="rounded-full" onClick={() => { setShowMfaDisable(false); setMfaDisablePassword(''); }}>
                          Annuler
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <Button variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowMfaDisable(true)}>
                      <ShieldOff className="w-4 h-4 mr-2" />
                      Désactiver
                    </Button>
                  )}
                </div>
              ) : (
                <Button onClick={startMfaSetup} disabled={mfaLoading} className="rounded-full bg-[#0596DE] hover:bg-[#047CBA]">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {mfaLoading ? 'Chargement...' : 'Activer la double authentification'}
                </Button>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
